import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  QrCode, Search, Check, X, Gift, Clock, Calendar,
  AlertCircle, User, Building2, ChevronRight, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import { formatDate, formatTime } from '@/lib/dateUtils';
import api from '@/lib/api';
import { toast } from 'sonner';

// Verification Result Card
const VerificationResult = ({ result, onConfirmRedeem, confirming }) => {
  if (!result) return null;
  
  const statusColors = {
    active: 'bg-green-500',
    reserved: 'bg-blue-500',
    redeemed: 'bg-gray-500',
    expired: 'bg-red-500'
  };
  
  return (
    <Card className="border-2 border-green-200 dark:border-green-800">
      <CardHeader className="bg-green-50 dark:bg-green-950/30">
        <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
          <Check className="w-6 h-6" />
          Voucher Verified
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
            <Gift className="w-8 h-8 text-purple-600" />
          </div>
          <div className="flex-grow">
            <h3 className="text-xl font-bold">{result.voucher_title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`${statusColors[result.status]} text-white`}>
                {result.status.toUpperCase()}
              </Badge>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Customer</p>
            <p className="font-medium flex items-center gap-2">
              <User className="w-4 h-4" />
              {result.customer_name}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Status</p>
            <p className="font-medium capitalize">{result.status}</p>
          </div>
          {result.reservation_date && (
            <>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Reserved Date</p>
                <p className="font-medium">{formatDate(result.reservation_date)}</p>
              </div>
              {result.reservation_time && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Reserved Time</p>
                  <p className="font-medium">{formatTime(result.reservation_time)}</p>
                </div>
              )}
            </>
          )}
        </div>
        
        {result.can_redeem ? (
          <Button 
            className="w-full h-14 bg-green-600 hover:bg-green-700 text-lg"
            onClick={() => onConfirmRedeem(result)}
            disabled={confirming}
            data-testid="confirm-redemption-btn"
          >
            {confirming ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Confirming...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Confirm Redemption
              </>
            )}
          </Button>
        ) : (
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-center">
            <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="font-medium text-amber-700 dark:text-amber-400">
              This voucher has already been {result.status}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Pending Voucher Row
const PendingVoucherRow = ({ voucher, onVerify }) => {
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:border-indigo-600/50 transition-colors">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
          <Gift className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h4 className="font-bold">{voucher.voucher_title}</h4>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <User className="w-3 h-3" />
            {voucher.user_name}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {voucher.reservation_date && (
          <div className="text-right">
            <p className="text-sm font-medium">{formatDate(voucher.reservation_date)}</p>
            {voucher.reservation_time && (
              <p className="text-xs text-muted-foreground">{formatTime(voucher.reservation_time)}</p>
            )}
          </div>
        )}
        <Badge className={voucher.status === 'reserved' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}>
          {voucher.status}
        </Badge>
        <Button variant="outline" size="sm" onClick={() => onVerify(voucher.redemption_code)}>
          Verify
        </Button>
      </div>
    </div>
  );
};

// Main Component
const PartnerRedemptions = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [pendingVouchers, setPendingVouchers] = useState([]);
  const [redeemedVouchers, setRedeemedVouchers] = useState([]);
  const [activeTab, setActiveTab] = useState('scan');

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    if (!isPartner) {
      navigate('/dashboard');
      return;
    }
    
    fetchData();
  }, [isAuthenticated, isPartner, authLoading]);

  const fetchData = async () => {
    try {
      const [pendingRes, redeemedRes] = await Promise.all([
        api.get('/redemptions/partner/pending'),
        api.get('/redemptions/partner/redeemed')
      ]);
      
      setPendingVouchers(pendingRes.data.pending_vouchers || []);
      setRedeemedVouchers(redeemedRes.data.redeemed_vouchers || []);
    } catch (error) {
      console.error('Failed to fetch redemption data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (inputCode = code) => {
    if (!inputCode.trim()) {
      toast.error('Please enter a redemption code');
      return;
    }
    
    setVerifying(true);
    setVerificationResult(null);
    
    try {
      const response = await api.get(`/redemptions/verify/${inputCode.trim().toUpperCase()}`);
      setVerificationResult(response.data);
      
      if (response.data.valid) {
        toast.success('Voucher verified successfully');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Invalid redemption code');
      } else if (error.response?.status === 403) {
        toast.error('This voucher is not for your venue');
      } else {
        toast.error(error.response?.data?.detail || 'Verification failed');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleConfirmRedeem = async (result) => {
    setConfirming(true);
    
    try {
      await api.post('/redemptions/confirm-redemption', {
        voucher_purchase_id: result.purchase_id,
        redemption_code: code.trim().toUpperCase() || result.redemption_code
      });
      
      toast.success('Voucher redeemed successfully!');
      setVerificationResult(null);
      setCode('');
      fetchData(); // Refresh lists
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm redemption');
    } finally {
      setConfirming(false);
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  return (
    <PartnerLayout
      title="Voucher Redemptions"
      subtitle="Verify and redeem customer vouchers"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="scan" className="flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              Scan & Verify
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending ({pendingVouchers.length})
            </TabsTrigger>
            <TabsTrigger value="redeemed" className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Redeemed ({redeemedVouchers.length})
            </TabsTrigger>
          </TabsList>

          {/* Scan & Verify Tab */}
          <TabsContent value="scan" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-indigo-600" />
                  Enter Redemption Code
                </CardTitle>
                <CardDescription>
                  Enter the 12-character code shown on the customer's device
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-grow">
                    <Input
                      placeholder="Enter code (e.g., ABC123XYZ789)"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="h-14 text-xl font-mono tracking-widest uppercase text-center"
                      maxLength={12}
                      data-testid="redemption-code-input"
                    />
                  </div>
                  <Button 
                    onClick={() => handleVerify()}
                    disabled={verifying || !code.trim()}
                    className="h-14 px-8 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="verify-code-btn"
                  >
                    {verifying ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Verify
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Verification Result */}
            {verificationResult && (
              <VerificationResult
                result={verificationResult}
                onConfirmRedeem={handleConfirmRedeem}
                confirming={confirming}
              />
            )}
          </TabsContent>

          {/* Pending Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Pending Vouchers</CardTitle>
                  <CardDescription>Active and reserved vouchers for your venue</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {pendingVouchers.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No pending vouchers</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingVouchers.map((voucher) => (
                      <PendingVoucherRow
                        key={voucher.id}
                        voucher={voucher}
                        onVerify={(code) => {
                          setCode(code);
                          setActiveTab('scan');
                          handleVerify(code);
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Redeemed Tab */}
          <TabsContent value="redeemed">
            <Card>
              <CardHeader>
                <CardTitle>Redeemed Vouchers</CardTitle>
                <CardDescription>History of redeemed vouchers</CardDescription>
              </CardHeader>
              <CardContent>
                {redeemedVouchers.length === 0 ? (
                  <div className="text-center py-12">
                    <Check className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No redeemed vouchers yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {redeemedVouchers.map((voucher) => (
                      <div key={voucher.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                            <Check className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-bold">{voucher.voucher_title}</h4>
                            <p className="text-sm text-muted-foreground">
                              {voucher.user_name} • Redeemed {formatDate(voucher.redeemed_at?.split('T')[0])}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-green-500 text-white">Redeemed</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </PartnerLayout>
  );
};

export default PartnerRedemptions;
